'use client';
import React from 'react';
import { api } from '@/trpc/react';
import { match } from 'ts-pattern';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Control, useController } from 'react-hook-form';
import { capitalize } from 'lodash';
import { Card, CardTitle } from '@components/ui/card';
import { Ban, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { GLASSES_VALUES } from '@data/constants';

interface WildcardStepProps {
  themeId: string;
  gender?: 'male' | 'female';
  wildcardModifierKey: string;
  category: string;
  control: Control<any>;
  setValue: any;
  showModelGlasses?: boolean;
  trainingImages?: string[];
}

/**
 * This component is hard coded to work with the modifier key "glasses".
 * In the future if we want to add more category ( atm we only have 'casual' ), we would need to update this logic
 */
export function GlassesWildcardItemSelectorStep({
  themeId,
  gender,
  category,
  control,
  setValue,
  showModelGlasses = false,
  trainingImages = []
}: WildcardStepProps) {
  const themeWithWildcardsQuery = api.modifiers.get.useQuery(
    { category, genderAssociation: gender ? [gender] : undefined },
    { enabled: !!themeId }
  );

  const { field } = useController({
    name: 'selectedModifiers',
    control,
    defaultValue: {}
  });

  const handleSelectionChange = (value: string) => {
    const currentModifiers = { ...field.value };

    if (value === GLASSES_VALUES.RANDOM) {
      currentModifiers['glasses'] = GLASSES_VALUES.RANDOM;
    } else if (value === GLASSES_VALUES.NONE) {
      if (showModelGlasses) {
        currentModifiers['glasses'] = GLASSES_VALUES.NONE;
      } else {
        delete currentModifiers['glasses'];
      }
    } else {
      currentModifiers['glasses'] = value;
    }

    setValue('selectedModifiers', currentModifiers);
  };

  const currentValue = field.value['glasses'] || 'None';

  // Function to get 4 random sample URLs
  const getRandomSampleUrls = (options: any[]) => {
    const allSampleUrls = options.flatMap(option => option.sampleImageUrls || []);
    const shuffled = allSampleUrls.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 4);
  };

  console.log('currentValue', currentValue);
  return (
    <div className="flex flex-col h-full">
      {match(themeWithWildcardsQuery)
        .with({ status: 'pending' }, () => (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        ))
        .with({ status: 'error' }, () => <div>Error loading options</div>)
        .with({ status: 'success' }, ({ data }) => {
          const options = data.filter(option => option.category === category) || [];

          const filteredOptions = options.filter(option => {
            const optionGender = option.gender_association.toLowerCase();
            const matchesGender = !gender || optionGender === gender.toLowerCase() || optionGender === 'gender_neutral';
            return matchesGender;
          });

          // Get 4 random sample URLs for the "Random" option
          const randomSampleUrls = getRandomSampleUrls(filteredOptions);

          return category === 'glasses' ? (
            <ScrollArea className="flex-grow -px-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-2 ">
                <Card
                  key="none"
                  className={`p-3 min-w-0 relative cursor-pointer ${
                    currentValue === 'None' ? 'ring-2 ring-muted-foreground/10' : ''
                  }`}
                  onClick={() => handleSelectionChange(GLASSES_VALUES.NONE)}
                >
                  <div className="relative grid place-items-center flex-1 aspect-square mb-2.5">
                    {currentValue === 'None' && (
                      <div className="absolute z-[1] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-blue-500 text-white rounded-full p-3 border-2 border-white shadow-md">
                        <Check size={16} />
                      </div>
                    )}
                    <Ban size={48} className="text-gray-400" />
                  </div>
                  <CardTitle className="text-sm text-center line-clamp-2">None</CardTitle>
                </Card>
                {showModelGlasses && (
                  <Card
                    key="model-glasses"
                    className={`p-3 min-w-0 relative cursor-pointer ${
                      currentValue === GLASSES_VALUES.MODEL_GLASSES ? 'ring-2 ring-muted-foreground/10' : ''
                    }`}
                    onClick={() => handleSelectionChange(GLASSES_VALUES.MODEL_GLASSES)}
                  >
                    <div className={cn('relative grid grid-cols-2 flex-1 gap-[1px] auto-rows-fr aspect-square mb-2.5')}>
                      {currentValue === GLASSES_VALUES.MODEL_GLASSES && (
                        <div className="absolute z-[1] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-blue-500 text-white rounded-full p-3 border-2 border-white shadow-md">
                          <Check size={16} />
                        </div>
                      )}
                      <img
                        src={trainingImages[0] || '/svgs/placeholder.svg'}
                        alt="placeholder"
                        className={cn(currentValue === GLASSES_VALUES.MODEL_GLASSES ? 'opacity-50' : '')}
                      />
                      <img
                        src={trainingImages[1] || '/svgs/placeholder.svg'}
                        alt="placeholder"
                        className={cn(currentValue === GLASSES_VALUES.MODEL_GLASSES ? 'opacity-50' : '')}
                      />
                      <img
                        src={trainingImages[2] || '/svgs/placeholder.svg'}
                        alt="placeholder"
                        className={cn(currentValue === GLASSES_VALUES.MODEL_GLASSES ? 'opacity-50' : '')}
                      />
                      <img
                        src={trainingImages[3] || '/svgs/placeholder.svg'}
                        alt="placeholder"
                        className={cn(currentValue === GLASSES_VALUES.MODEL_GLASSES ? 'opacity-50' : '')}
                      />
                    </div>
                    <CardTitle className="text-sm text-center line-clamp-2">My glasses</CardTitle>
                  </Card>
                )}
                <Card
                  key="random"
                  className={`p-3 min-w-0 relative cursor-pointer ${
                    currentValue === 'Random' ? 'ring-2 ring-muted-foreground/10' : ''
                  }`}
                  onClick={() => handleSelectionChange('Random')}
                >
                  <div className="relative grid grid-cols-2 flex-1 gap-[1px] auto-rows-fr aspect-square mb-2.5">
                    {currentValue === 'Random' && (
                      <div className="absolute z-[1] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-blue-500 text-white rounded-full p-3 border-2 border-white shadow-md">
                        <Check size={16} />
                      </div>
                    )}
                    {randomSampleUrls.map((url, index) => (
                      <img
                        key={index}
                        src={url || '/svgs/placeholder.svg'}
                        alt={`Random sample ${index + 1}`}
                        className={cn(currentValue === 'Random' ? 'opacity-50' : '')}
                      />
                    ))}
                  </div>
                  <CardTitle className="text-sm text-center line-clamp-2">Random</CardTitle>
                </Card>

                {filteredOptions.map(option => (
                  <Card
                    key={option.id}
                    className={cn(
                      'p-3 min-w-0 cursor-pointer relative',
                      currentValue === option.value ? 'ring-2 ring-muted-foreground/10' : ''
                    )}
                    onClick={() => handleSelectionChange(option.value || '')}
                  >
                    <div
                      className={cn(
                        'relative grid grid-cols-2 flex-1 gap-[1px] auto-rows-fr aspect-square mb-2.5',
                        currentValue === option.value ? '' : ''
                      )}
                    >
                      {currentValue === option.value && (
                        <div className="absolute z-[1] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-blue-500 text-white rounded-full p-3 border-2 border-white shadow-md">
                          <Check size={16} />
                        </div>
                      )}
                      <img
                        src={option.sampleImageUrls?.[0] ?? '/svgs/placeholder.svg'}
                        alt="placeholder"
                        className={cn(currentValue === option.value ? 'opacity-50' : '')}
                      />
                      <img
                        src={option.sampleImageUrls?.[1] ?? '/svgs/placeholder.svg'}
                        alt="placeholder"
                        className={cn(currentValue === option.value ? 'opacity-50' : '')}
                      />
                      <img
                        src={option.sampleImageUrls?.[2] ?? '/svgs/placeholder.svg'}
                        alt="placeholder"
                        className={cn(currentValue === option.value ? 'opacity-50' : '')}
                      />
                      <img
                        src={option.sampleImageUrls?.[3] ?? '/svgs/placeholder.svg'}
                        alt="placeholder"
                        className={cn(currentValue === option.value ? 'opacity-50' : '')}
                      />
                    </div>
                    <CardTitle className="text-sm text-center line-clamp-2">{capitalize(option.value || '')}</CardTitle>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <RadioGroup
              value={currentValue}
              onValueChange={handleSelectionChange}
              className="divide-y-[1px] divide-border flex flex-col gap-0"
            >
              <div className="flex items-center space-x-2 py-2">
                <RadioGroupItem value="Random" id="wildcard-random" variant="check" />
                <Label htmlFor="wildcard-random" className="text-base">
                  Random
                </Label>
              </div>
              {filteredOptions.map(option => (
                <div key={option.id} className="flex items-center space-x-2 py-2">
                  <RadioGroupItem value={option.value || ''} id={`wildcard-${option.id}`} variant="check" />
                  <Label htmlFor={`wildcard-${option.id}`} className="text-base">
                    {capitalize(option.value || '')}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          );
        })
        .exhaustive()}
    </div>
  );
}
